'use client';

import { c as _c } from "react/compiler-runtime";
import type { AccommodationRate } from '@data-access/graphql/schema.types';
import type { AvailableRoom } from '@feat/feat-accommodation/availability';
import { getInput, mapAccommodationPaxInput, useAvailableRooms } from '@feat/feat-accommodation/availability';
import type { AccommodationMutationInput } from '@feat/feat-accommodation/booking';
import { CalendarX } from '@phosphor-icons/react';
import { shopTestId } from '@utils/utils-shop-test-ids';
import type { AccommodationConfig } from '../_lib/accommodation-config';
import { AccommodationRoomSkeleton } from './accommodation-room-skeleton';
import { AppAccommodationAvailableRoom, type AppAccommodationAvailableRoomResult } from './app-accommodation-available-room';
type AppAccommodationAvailableRoomsContainerProps = {
  accommodation: AccommodationMutationInput;
  config: AccommodationConfig;
  onSubmit?: (result?: AppAccommodationAvailableRoomResult) => Promise<void>;
  onSelectRate?: (rate: AccommodationRate, room: AvailableRoom) => void;
  selectedRate?: string;
  showRoomCount?: boolean;
};
export function AppAccommodationAvailableRoomsContainer(t0) {
  const $ = _c(30);
  const {
    accommodation,
    config,
    onSubmit,
    onSelectRate,
    selectedRate,
    showRoomCount
  } = t0;
  let t1;
  if ($[0] !== accommodation.id || $[1] !== config.adults || $[2] !== config.children || $[3] !== config.from || $[4] !== config.to) {
    t1 = getInput({
      accommodationId: accommodation.id,
      allocation: mapAccommodationPaxInput({
        adults: config.adults,
        children: config.children
      }),
      to: config.to,
      from: config.from
    });
    $[0] = accommodation.id;
    $[1] = config.adults;
    $[2] = config.children;
    $[3] = config.from;
    $[4] = config.to;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    data: t2,
    isLoading
  } = useAvailableRooms(t1);
  let t3;
  if ($[6] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const rooms = t3;
  if (isLoading) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AccommodationRoomSkeleton />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (!rooms.length) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <EmptyState />;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t4;
  if ($[10] !== rooms) {
    t4 = rooms.reduce(_temp);
    $[10] = rooms;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const cheapestRoomId = t4.id;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = shopTestId("AccommodationAvailableRooms.Container");
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== accommodation || $[14] !== cheapestRoomId || $[15] !== onSelectRate || $[16] !== onSubmit || $[17] !== rooms || $[18] !== selectedRate || $[19] !== showRoomCount) {
    let t7;
    if ($[21] !== accommodation || $[22] !== cheapestRoomId || $[23] !== onSelectRate || $[24] !== onSubmit || $[25] !== selectedRate || $[26] !== showRoomCount) {
      t7 = room_0 => <li key={room_0.id} className="bleed-container full-bleed gap-y-4 sm:grid-cols-12 sm:gap-4 md:gap-8" data-testid={shopTestId("AccommodationAvailableRoom.Container")}><AppAccommodationAvailableRoom room={room_0} accommodation={accommodation} isCheapestRoom={room_0.id === cheapestRoomId} onSubmit={onSubmit} onSelectRate={onSelectRate} selectedRate={selectedRate} showRoomCount={showRoomCount} /></li>;
      $[21] = accommodation;
      $[22] = cheapestRoomId;
      $[23] = onSelectRate;
      $[24] = onSubmit;
      $[25] = selectedRate;
      $[26] = showRoomCount;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    t6 = rooms.map(t7);
    $[13] = accommodation;
    $[14] = cheapestRoomId;
    $[15] = onSelectRate;
    $[16] = onSubmit;
    $[17] = rooms;
    $[18] = selectedRate;
    $[19] = showRoomCount;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[28] !== t6) {
    t7 = <ul className="bleed-container full-bleed divide-primary/15 *:py-7 *:first:pt-0 *:last:pb-0 sm:col-span-1 sm:col-start-2 sm:divide-y sm:*:py-8" data-testid={t5}>{t6}</ul>;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
}
function _temp(cheapest, room) {
  return room.lowestRate.price < cheapest.lowestRate.price ? room : cheapest;
}
function EmptyState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="fluid-grid pt-10 sm:pt-0"><div className="col-span-6 flex flex-col items-center sm:col-start-4"><CalendarX size={32} /><h2 className="mt-4 mb-2 text-center font-heading3">Keine freien Zimmer gefunden</h2><p className="px-6 text-center font-body-copy">Leider haben wir zu deiner Suche keine freien Zimmer mehr verfügbar. Bitte pass deine Auswahl an.</p></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}